import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { teamAPI, graphicAPI } from "../services/api";
import GraphicCard from "../components/GraphicCard";
import ScrollHint from "../components/ScrollHint";
import Footer from "../components/Footer";
import { getLogoSrc } from "../utils/logoHelper";
import api from "../services/api";
import "../App.css";
import "./TeamPortal.css";

const API_BASE_URL = api.defaults.baseURL.replace("/api", "");

function TeamPortal() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [graphics, setGraphics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [graphicsLimitInfo, setGraphicsLimitInfo] = useState(null);
  const [graphicTypeOpen, setGraphicTypeOpen] = useState({});
  const [showCoachBanner, setShowCoachBanner] = useState(false);
  const [toastMessage, setToastMessage] = useState({ type: "", text: "" });
  // Helper function to show toast messages
  const showToast = (type, text, duration = 3000) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage({ type: "", text: "" }), duration);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [teamRes, graphicsRes, allTeamsRes, limitRes] = await Promise.all(
          [
            teamAPI.getById(teamId),
            graphicAPI.getAll(),
            teamAPI.getAll(),
            graphicAPI.getLimitInfo(),
          ],
        );

        setTeam(teamRes.data);
        // Filter to accessible teams only
        const allNonDeletedTeams = allTeamsRes.data.filter(
          (t) => t.deletedAt === null,
        );
        const orgTeams = allNonDeletedTeams.filter(
          (t) => t.organizationTeam === true,
        );
        const personalTeams = allNonDeletedTeams.filter(
          (t) => !t.organizationTeam,
        );

        // Fetch team limit
        const teamLimitRes = await teamAPI.getLimitInfo();
        const teamLimit = teamLimitRes.data.teamLimit || Infinity;

        // Sort personal teams oldest first, take first N accessible
        const sortedPersonal = [...personalTeams].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
        const accessiblePersonal = sortedPersonal.slice(0, teamLimit);

        // Combine org + accessible personal
        setAllTeams([...orgTeams, ...accessiblePersonal]);
        setGraphicsLimitInfo(limitRes.data);

        // Only this team's graphics
        const teamGraphics = graphicsRes.data.filter(
          (g) => g.teamId === teamId,
        );
        setGraphics(teamGraphics);
        // Check if this is an assigned coach team and banner hasn't been dismissed
        if (teamRes.data.isTeamCoach) {
          const dismissed = localStorage.getItem("coachBannerDismissed");
          if (!dismissed) {
            setShowCoachBanner(true);
          }
        }
      } catch (error) {
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [teamId, navigate]);

  const dismissCoachBanner = () => {
    setShowCoachBanner(false);
    localStorage.setItem("coachBannerDismissed", "true");
  };

  useEffect(() => {
    // Check all carousels for overflow
    const carousels = document.querySelectorAll(".graphics-carousel");
    carousels.forEach((carousel) => {
      const wrapper = carousel.parentElement;
      if (carousel.scrollWidth > carousel.clientWidth) {
        wrapper.classList.add("has-scroll");
      } else {
        wrapper.classList.remove("has-scroll");
      }
    });
  }, [graphics]);

  useEffect(() => {
    // Auto-expand section based on URL hash
    if (window.location.hash) {
      const templateType = window.location.hash.replace("#", "");
      setGraphicTypeOpen((prev) => ({
        ...prev,
        [templateType]: true,
      }));

      // Scroll to section after render
      setTimeout(() => {
        const section = document.querySelector(
          `[data-template-type="${templateType}"]`,
        );
        if (section) {
          section.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [graphics]);

  const handleDeleteGraphic = async (id) => {
    try {
      await graphicAPI.delete(id);
      setGraphics(graphics.filter((g) => g.id !== id));
    } catch (err) {
      console.error("Error deleting graphic:", err);
      showToast("error", "Failed to delete graphic");
    }
  };

  if (loading) {
    return (
      <div className="team-portal-page">
        <Navbar />
        <div className="team-portal-loading">Loading team...</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="team-portal-page">
        <Navbar />
        <div className="team-portal-error">Team not found</div>
      </div>
    );
  }

  const subscriptionPlan = graphicsLimitInfo?.subscriptionPlan || "free";
  const graphicsLimit = graphicsLimitInfo?.graphicsLimit || Infinity;

  // Sort all graphics by date
  const sortedGraphics = [...graphics].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  // Calculate starting count from OTHER accessible teams
  // For org teams: always 0 (unlimited)
  // For personal teams: count plan-accessible graphics from other accessible teams
  let accessibleCount = 0;
  if (!team.organizationTeam) {
    // Get accessible personal team IDs (allTeams already filtered to accessible)
    // eslint-disable-next-line
    const otherAccessibleTeamIds = allTeams
      .filter((t) => !t.organizationTeam && t.id !== team.id)
      .map((t) => t.id);

    // We need all graphics to count from other teams
    // But we only have THIS team's graphics in state
    // So we use the backend count and subtract this team's graphics
    // Then adjust for plan restrictions
    const backendTotalCount = graphicsLimitInfo?.currentGraphics || 0;
    const thisTeamTotalCount = graphics.length;

    // Start with backend's count of other teams
    let otherTeamsRawCount = Math.max(
      0,
      backendTotalCount - thisTeamTotalCount,
    );

    // For Free tier: backend might count highlights/tryouts from other teams
    // We need to estimate and reduce the count proportionally
    if (subscriptionPlan === "free") {
      // Free users can't have highlights/tryouts, so backend count should be accurate
      // But if they downgraded, some locked graphics might be counted
      // We'll trust the count for now since Free = 1 team (no "other teams")
      accessibleCount = 0; // Free tier = 1 team limit, so no other teams exist
    } else {
      // Pro/Premium: use the calculated count from other teams
      accessibleCount = otherTeamsRawCount;
    }
  }

  // Mark which graphics are locked
  const graphicsWithLockState = sortedGraphics.map((graphic) => {
    let isLocked = false;
    let lockReason = "subscription";

    // Check org template restrictions first (if org team)
    if (team.organizationTeam && team.orgTemplateSettings) {
      // Template type restrictions
      if (
        graphic.templateType === "tryout" &&
        !team.orgTemplateSettings.allowTryouts
      ) {
        isLocked = true; // Locked by org settings
        lockReason = "org-admin";
      }
      if (
        graphic.templateType === "highlight" &&
        !team.orgTemplateSettings.allowPlayerHighlights
      ) {
        isLocked = true; // Locked by org settings
        lockReason = "org-admin";
      }

      // Design variation restrictions
      if (
        graphic.template === "splitScreen" &&
        !team.orgTemplateSettings.allowSplitScreen
      ) {
        isLocked = true; // Locked by org settings
        lockReason = "org-admin";
      }
      if (graphic.template === "bold" && !team.orgTemplateSettings.allowBold) {
        isLocked = true; // Locked by org settings
        lockReason = "org-admin";
      }
      if (
        graphic.template === "minimal" &&
        !team.orgTemplateSettings.allowMinimal
      ) {
        isLocked = true; // Locked by org settings
        lockReason = "org-admin";
      }
      if (
        graphic.template === "photoFrame" &&
        !team.orgTemplateSettings.allowPhotoFrame
      ) {
        isLocked = true; // Locked by org settings
        lockReason = "org-admin";
      }
    }

    // Then check plan restrictions (skip for org teams)
    // All template types are available on free tier

    // Pro and Premium: all graphic types unlocked, only limited by count
    // If not locked by plan, check if within limit
    if (!isLocked) {
      if (accessibleCount >= graphicsLimit) {
        isLocked = true; // Locked by limit
      } else {
        accessibleCount++; // Count towards limit
      }
    }

    return {
      ...graphic,
      isLocked,
      lockReason,
    };
  });

  // Org teams never hit limit, personal teams check against accessible graphics count
  const hasReachedLimit = team.organizationTeam
    ? false
    : accessibleCount >= graphicsLimit;

  // Group by type
  const templateTypeConfig = {
    preview: { icon: "", label: "Game Preview", order: 1 },
    recap: { icon: "", label: "Game Recap", order: 2 },
    tryout: { icon: "", label: "Tryouts", order: 3 },
    highlight: { icon: "", label: "Player Spotlight", order: 4 },
  };

  const groupedGraphics = Object.keys(templateTypeConfig).reduce(
    (acc, type) => {
      acc[type] = [];
      return acc;
    },
    {},
  );

  graphicsWithLockState.forEach((graphic) => {
    const type = graphic.templateType;
    if (groupedGraphics[type]) {
      groupedGraphics[type].push(graphic);
    }
  });

  const sortedTypes =
    graphicsWithLockState.length > 0
      ? Object.keys(templateTypeConfig).sort(
          (a, b) =>
            (templateTypeConfig[a]?.order || 999) -
            (templateTypeConfig[b]?.order || 999),
        )
      : [];

  return (
    <div className="team-portal-page">
      <Navbar />

      {/* Coach Assignment Banner */}
      {showCoachBanner && (
        <div className="coach-info-banner">
          <div className="banner-content">
            <span className="banner-icon">ℹ️</span>
            <p>
              You're coaching this organization team with full access to all
              features. Your personal teams use your {subscriptionPlan} tier
              limits.
            </p>
          </div>
          <button className="banner-dismiss" onClick={dismissCoachBanner}>
            ✕
          </button>
        </div>
      )}

      <div
        className="team-portal-header"
        style={{
          background: `linear-gradient(135deg, ${
            team.primaryColor || team.orgPrimaryColor || "#003366"
          } 0%, ${
            team.secondaryColor || team.orgSecondaryColor || "#FFFFFF"
          } 100%)`,
        }}
      >
        <div className="team-header-content">
          <div className="team-header-left">
            {(team.logoUrl || (team.organizationTeam && team.orgLogoUrl)) && (
              <img
                src={getLogoSrc(team.logoUrl || team.orgLogoUrl, API_BASE_URL)}
                alt={team.name}
                className="team-portal-logo"
              />
            )}
            <div className="team-header-info">
              <h1>{team.name}</h1>
              <p>
                {team.ageGroup && `${team.ageGroup} • `}
                {team.sport && `${team.sport} • `}
                {team.season}
              </p>
            </div>
          </div>

          {allTeams.length > 1 && (
            <div className="team-switcher">
              <label>Switch Team:</label>
              <select
                value={teamId}
                onChange={(e) => navigate(`/team/${e.target.value}`)}
                className="team-select"
              >
                {allTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.orgAcronym ? ` (${t.orgAcronym})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="team-actions-bar">
        {hasReachedLimit && !team.organizationTeam ? (
          <button
            className="btn btn-upgrade-roster"
            onClick={() => navigate("/membership")}
          >
            ⭐ Upgrade for More Graphics
          </button>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/create-graphic?teamId=${team.id}`)}
          >
            + Create Graphic
          </button>
        )}
        <button
          className="btn btn-primary"
          onClick={() => navigate(`/edit-team/${team.id}`)}
        >
          ✏️ Edit Team
        </button>
        {team.organizationTeam || subscriptionPlan !== "free" ? (
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/edit-team/${team.id}#roster`)}
          >
            👥 Manage Roster
          </button>
        ) : (
          <button
            className="btn btn-upgrade-roster"
            onClick={() => navigate("/membership")}
          >
            🔒 Upgrade to Manage Roster
          </button>
        )}
      </div>

      <div className="team-portal-content">
        <section className="team-section">
          <div className="section-header">
            <h2>
              Total Team Graphics Created ({accessibleCount}
              {graphicsLimit !== Infinity && `/${graphicsLimit}`})
            </h2>
          </div>

          {graphicsWithLockState.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📸</div>
              <h3>No Graphics Yet</h3>
              <p>Create your first graphic for {team.name}!</p>
              {hasReachedLimit ? (
                <button
                  className="btn btn-upgrade-roster"
                  onClick={() => navigate("/membership")}
                >
                  ⭐ Upgrade for More Graphics
                </button>
              ) : (
                <button
                  className="btn btn-success"
                  onClick={() => navigate(`/create-graphic?teamId=${team.id}`)}
                >
                  + Create Graphic
                </button>
              )}
            </div>
          ) : (
            <div className="graphics-organized">
              {sortedTypes.map((type) => {
                const config = templateTypeConfig[type];
                return (
                  <div
                    key={type}
                    className="graphics-type-section"
                    data-template-type={type}
                  >
                    <div
                      className="graphics-type-header"
                      onClick={() => {
                        setGraphicTypeOpen((prev) => ({
                          ...prev,
                          [type]: !prev[type],
                        }));
                      }}
                    >
                      <h4>
                        {config.icon} {config.label} (
                        {groupedGraphics[type].filter((g) => !g.isLocked)
                          .length || 0}
                        )
                      </h4>
                      <button className="type-collapse-toggle">
                        {graphicTypeOpen[type] !== true ? "+" : "-"}
                      </button>
                    </div>
                    {graphicTypeOpen[type] === true && (
                      <div className="graphics-carousel-wrapper">
                        <ScrollHint />
                        <div className="graphics-carousel">
                          {(groupedGraphics[type] || []).map((graphic) => (
                            <GraphicCard
                              key={graphic.id}
                              graphic={{
                                ...graphic,
                                Team: team,
                              }}
                              onDelete={handleDeleteGraphic}
                              isLocked={graphic.isLocked}
                              lockReason={graphic.lockReason}
                              onUpgradeClick={() => navigate("/membership")}
                            />
                          ))}
                        </div>
                        {/* Empty state message */}
                        {groupedGraphics[type].length === 0 && (
                          <div className="empty-carousel-message">
                            <span className="empty-icon">{config.icon}</span>

                            {/* Check if org admin disabled this template type */}
                            {team.organizationTeam &&
                            team.orgTemplateSettings &&
                            ((type === "tryout" &&
                              !team.orgTemplateSettings.allowTryouts) ||
                              (type === "highlight" &&
                                !team.orgTemplateSettings
                                  .allowPlayerHighlights)) ? (
                              <>
                                <p>
                                  <strong>{config.label}</strong> graphics are
                                  disabled by your organization administrator
                                </p>
                                <p className="disabled-hint">
                                  Contact your org admin to enable this feature
                                </p>
                              </>
                            ) : (
                              <>
                                <p>
                                  Create your first{" "}
                                  <strong>{config.label}</strong> here
                                </p>
                                {hasReachedLimit ? (
                                  <button
                                    className="btn btn-upgrade btn-sm"
                                    onClick={() => navigate("/membership")}
                                  >
                                    ⭐ Upgrade Plan
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() =>
                                      navigate(
                                        `/create-graphic?teamId=${team.id}&templateType=${type}`,
                                      )
                                    }
                                  >
                                    + Create {config.label}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
      {/* Toast Notifications */}
      {toastMessage.text && toastMessage.type === "success" && (
        <div className="toast-message toast-success">{toastMessage.text}</div>
      )}
      {toastMessage.text && toastMessage.type === "error" && (
        <div className="toast-message toast-error">{toastMessage.text}</div>
      )}
      <Footer />
    </div>
  );
}

export default TeamPortal;
